import { useEffect } from "react";
import {
  ArrowRight,
  Calendar,
  Check,
  ChevronRight,
  Clock,
  HeartHandshake,
  Lock,
  Moon,
  ShieldCheck,
  Smartphone,
  Sparkles,
  TimerReset
} from "lucide-react";
import { captureMarketingEvent, capturePageView } from "./analytics.js";
import nextPreview from "./assets/screenshot-next.svg";
import timelinePreview from "./assets/screenshot-timeline.svg";
import insightsPreview from "./assets/screenshot-insights.svg";
import { privacyPolicy, termsOfService } from "./legalContent.js";

const APP_STORE_URL = "COMING_SOON";
const appIcon = "/assets/app-icon.png";

const routineSteps = [
  {
    eyebrow: "1",
    title: "Log the moment",
    body: "Tap the feed, wake, sleep, diaper, or cue that just happened. BabyNext keeps the rhythm easy to review.",
    Icon: TimerReset
  },
  {
    eyebrow: "2",
    title: "See the rhythm",
    body: "The day becomes a simple feed-wake-sleep record, with day/night rhythm and routine cues easy to review.",
    Icon: Calendar
  },
  {
    eyebrow: "3",
    title: "See the next step",
    body: "Next Up shows what fits the current rhythm, why it fits, and when baby cues or provider guidance should lead.",
    Icon: Sparkles
  }
];

const productPillars = [
  {
    title: "Feed-wake-sleep rhythm",
    body: "Use a flexible rhythm as a guide, not a rule. Feeding, wake time, and rest stay connected in one clear view.",
    Icon: Clock
  },
  {
    title: "One clear Next Up",
    body: "Open the app and see the next care step, with the reason behind it.",
    Icon: Moon
  },
  {
    title: "A readable day",
    body: "Timeline and Insights turn scattered logs into fuller context for your family and appointments.",
    Icon: Calendar
  },
  {
    title: "Shared care context",
    body: "Partners and babysitters can understand the current rhythm without needing a long explanation.",
    Icon: HeartHandshake
  }
];

const privacyPoints = [
  "A flexible rhythm helps parents feel oriented without rigid schedules.",
  "Routine guidance is computed on device.",
  "No BabyNext account or BabyNext server is required.",
  "Baby cues, hunger, distress, illness, and provider guidance come first."
];

function App() {
  const isStoreAvailable = APP_STORE_URL !== "COMING_SOON";
  const path = window.location.pathname.replace(/\/$/, "") || "/";
  const legalPage = path === "/privacy" ? privacyPolicy : path === "/terms" ? termsOfService : null;
  const pageTitle = legalPage ? `BabyNext ${legalPage.title}` : "BabyNext | Baby Routine Guidance";

  useEffect(() => {
    document.title = pageTitle;
    capturePageView();
  }, [pageTitle]);

  if (legalPage) {
    return (
      <div className="app-shell">
        <SiteHeader isStoreAvailable={isStoreAvailable} />
        <main>
          <LegalPage page={legalPage} />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="app-shell">
      <SiteHeader isStoreAvailable={isStoreAvailable} />
      <main>
        <Hero isStoreAvailable={isStoreAvailable} />
        <SignalStrip />
        <RoutineLoop />
        <ProductShowcase />
        <FeatureMatrix />
        <PrivacySection />
        <ClosingCta isStoreAvailable={isStoreAvailable} />
      </main>
      <Footer />
    </div>
  );
}

function SiteHeader({ isStoreAvailable }) {
  return (
    <header className="site-header">
      <a className="brand-mark" href="/" aria-label="BabyNext home">
        <img src={appIcon} alt="" width="42" height="42" />
        <span>BabyNext</span>
      </a>
      <nav className="site-nav" aria-label="Page sections">
        <a href="/#loop">The plan</a>
        <a href="/#features">Features</a>
        <a href="/#privacy">Privacy</a>
      </nav>
      <StoreButton compact isStoreAvailable={isStoreAvailable} />
    </header>
  );
}

function Hero({ isStoreAvailable }) {
  return (
    <section className="hero-section">
      <div className="hero-copy">
        <div className="launch-note">
          <Sparkles size={16} aria-hidden="true" />
          <span>Flexible newborn rhythm</span>
        </div>
        <h1>A clearer baby routine, one next step at a time.</h1>
        <p className="hero-lede">
          BabyNext helps tired newborn parents follow a flexible feed-wake-sleep
          rhythm, track the day, and see the next care step with less guesswork.
        </p>
        <div className="hero-actions">
          <StoreButton isStoreAvailable={isStoreAvailable} />
          <a
            className="text-action"
            href="#loop"
            onClick={() => captureMarketingEvent("simple plan link clicked", { placement: "hero" })}
          >
            See the simple plan
            <ArrowRight size={18} aria-hidden="true" />
          </a>
        </div>
        <div className="hero-proof" aria-label="BabyNext product highlights">
          <span>Feed-wake-sleep rhythm</span>
          <span>Baby-responsive</span>
          <span>No rigid schedules</span>
        </div>
      </div>

      <div className="hero-stage" aria-label="BabyNext app previews">
        <div className="phone-column phone-column-left">
          <img src={timelinePreview} alt="BabyNext Timeline preview with readable baby-care logs." />
        </div>
        <div className="phone-column phone-column-center">
          <img src={nextPreview} alt="BabyNext Next tab preview with a feed suggestion." />
        </div>
        <div className="floating-panel rhythm-panel">
          <span className="panel-kicker">Current rhythm</span>
          <strong>Awake 58 min</strong>
          <span>Next Up shows what fits now.</span>
        </div>
        <div className="floating-panel privacy-panel">
          <Lock size={17} aria-hidden="true" />
          <span>On-device guidance</span>
        </div>
      </div>
    </section>
  );
}

function StoreButton({ compact = false, isStoreAvailable }) {
  const label = isStoreAvailable ? "Download on the" : "Coming soon to the";
  const mainLabel = !isStoreAvailable && compact ? "Coming soon" : "App Store";

  return (
    <a
      className={`store-button ${compact ? "store-button-compact" : ""} ${!isStoreAvailable ? "is-disabled" : ""}`}
      href={isStoreAvailable ? APP_STORE_URL : "#download"}
      aria-disabled={!isStoreAvailable}
      data-app-store-link
      onClick={() =>
        captureMarketingEvent("app store cta clicked", {
          available: isStoreAvailable,
          placement: compact ? "header" : "body"
        })
      }
    >
      <Smartphone size={compact ? 17 : 21} aria-hidden="true" />
      <span>
        <small>{label}</small>
        {mainLabel}
      </span>
    </a>
  );
}

function SignalStrip() {
  return (
    <section className="signal-strip" aria-label="BabyNext value statement">
      <div>
        <span>Newborn days blur together.</span>
        <strong>BabyNext keeps the rhythm visible so you can care with more confidence.</strong>
      </div>
      <a href="#features">
        See what gets easier
        <ChevronRight size={18} aria-hidden="true" />
      </a>
    </section>
  );
}

function RoutineLoop() {
  return (
    <section className="content-section" id="loop">
      <div className="section-heading">
        <span className="section-kicker">The simple plan</span>
        <h2>Stop guessing what comes next.</h2>
        <p>
          A flexible feed-wake-sleep rhythm can make baby care feel less random.
          BabyNext helps you follow that rhythm while still responding to the baby
          in front of you.
        </p>
      </div>
      <div className="routine-grid">
        {routineSteps.map(({ eyebrow, title, body, Icon }) => (
          <article className="routine-card" key={title}>
            <Icon size={22} aria-hidden="true" />
            <span>{eyebrow}</span>
            <h3>{title}</h3>
            <p>{body}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function ProductShowcase() {
  return (
    <section className="showcase-section">
      <div className="showcase-copy">
        <span className="section-kicker">Your guide in the moment</span>
        <h2>Know where you are in the day.</h2>
        <p>
          Instead of asking a tired parent to remember every interval, BabyNext
          shows the last feed, wake time, sleep, diapers, and routine cues in one
          place so the next choice has fuller context.
        </p>
        <div className="metric-row" aria-label="Example daily summaries">
          <div>
            <strong>6</strong>
            <span>feeds</span>
          </div>
          <div>
            <strong>3</strong>
            <span>naps</span>
          </div>
          <div>
            <strong>7</strong>
            <span>diapers</span>
          </div>
        </div>
      </div>
      <div className="insights-display">
        <img src={insightsPreview} alt="BabyNext Insights preview with simple pattern summaries." />
      </div>
    </section>
  );
}

function FeatureMatrix() {
  return (
    <section className="content-section feature-section" id="features">
      <div className="section-heading">
        <span className="section-kicker">What gets easier</span>
        <h2>Feel oriented, not over-managed.</h2>
      </div>
      <div className="feature-matrix">
        {productPillars.map(({ title, body, Icon }) => (
          <article className="feature-item" key={title}>
            <Icon size={24} aria-hidden="true" />
            <div>
              <h3>{title}</h3>
              <p>{body}</p>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function PrivacySection() {
  return (
    <section className="privacy-section" id="privacy">
      <div className="privacy-card">
        <div className="privacy-icon">
          <ShieldCheck size={34} aria-hidden="true" />
        </div>
        <span className="section-kicker">Privacy and safety</span>
        <h2>A routine guide, not a rulebook.</h2>
        <p>
          BabyNext supports a flexible rhythm where hunger, distress, illness, and
          pediatric guidance still come first. It helps organize routine information;
          it is not medical advice.
        </p>
      </div>
      <div className="privacy-list">
        {privacyPoints.map((point) => (
          <div className="privacy-point" key={point}>
            <Check size={18} aria-hidden="true" />
            <span>{point}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function ClosingCta({ isStoreAvailable }) {
  return (
    <section className="closing-cta" id="download">
      <div>
        <span className="section-kicker">Coming soon</span>
        <h2>End the day with a readable record and a clearer sense of what happened.</h2>
        <p>
          The App Store button will point to the public BabyNext listing as soon as
          it is available.
        </p>
      </div>
      <StoreButton isStoreAvailable={isStoreAvailable} />
    </section>
  );
}

function Footer() {
  return (
    <footer className="site-footer">
      <a className="brand-mark" href="/" aria-label="BabyNext home">
        <img src={appIcon} alt="" width="36" height="36" />
        <span>BabyNext</span>
      </a>
      <div className="footer-links" aria-label="Footer links">
        <a href="/#loop">The plan</a>
        <a href="/#features">Features</a>
        <a href="/privacy">Privacy</a>
        <a href="/terms">Terms</a>
      </div>
      <p>Flexible newborn rhythm for tired parents.</p>
    </footer>
  );
}

function LegalPage({ page }) {
  return (
    <article className="legal-page">
      <div className="legal-heading">
        <span className="section-kicker">BabyNext</span>
        <h1>{page.title}</h1>
        <p>Effective Date: {page.effectiveDate}</p>
        <p>Website: https://babynext.app</p>
      </div>

      <div className="legal-body">
        {page.intro.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}

        {page.sections.map((section) => (
          <section key={section.title}>
            <h2>{section.title}</h2>
            {section.body?.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
            {section.list ? (
              <ul>
                {section.list.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            ) : null}
            {section.after?.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
            {section.contact ? (
              <address>
                {section.contact.map((line) => (
                  <span key={line}>{line}</span>
                ))}
              </address>
            ) : null}
          </section>
        ))}
      </div>
    </article>
  );
}

export default App;
